import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import { TruncatedTextTooltip } from "../../TruncatedTextTooltip/TruncatedTextTooltip";
import {
  selectCrmLeadsFilters,
  setCrmLeadsLeadStage,
} from "../../../redux/slices/filterSlice";
import { useGetAllLeadStagesQuery } from "../../../redux/services/crmApi";
import PulseLoader from "../../Loader/PulseLoader";

interface LeadStage {
  code: string;
  name: string;
  base_color?: string;
}

interface LeadStageFilterProps {
  onClose: () => void;
}

const LeadStageFilter = ({ onClose }: LeadStageFilterProps) => {
  const dispatch = useDispatch();
  const leadStage = useSelector(selectCrmLeadsFilters)?.leadStage;

  const { data: leadStagesData, isLoading: isLeadStagesLoading } =
    useGetAllLeadStagesQuery({});

  const stages = useMemo<LeadStage[]>(
    () =>
      Array.isArray(leadStagesData) ? (leadStagesData as LeadStage[]) : [],
    [leadStagesData]
  );

  const [selectedStages, setSelectedStages] = useState<string[]>(
    Array.isArray(leadStage?.value) ? [...leadStage.value] : []
  );

  const handleToggleStage = (code: string) => {
    if (!code) return;
    setSelectedStages((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleApply = () => {
    const codeToName = new Map(
      stages.map((s) => [s?.code, s?.name ?? s?.code])
    );
    const displayValues = selectedStages.map(
      (code) => codeToName.get(code) ?? code
    );
    dispatch(
      setCrmLeadsLeadStage({
        value: selectedStages,
        displayValue: displayValues,
      })
    );
    onClose();
  };

  const isApplyDisabled =
    JSON.stringify([...selectedStages].sort()) ===
    JSON.stringify([...(leadStage?.value || [])].sort());

  return (
    <div className="flex flex-col h-[92vh] overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        {selectedStages.length > 0 && (
          <span className="text-xxs font-semibold uppercase text-dark-gray dark:text-dark-gray-dk px-3 py-3">
            Selected ({selectedStages.length})
          </span>
        )}

        <div
          className={`flex-1 min-h-0 overflow-y-auto px-3 pb-3 ${
            selectedStages.length === 0 ? "mt-3" : ""
          }`}
        >
          {isLeadStagesLoading ? (
            <div className="flex items-center justify-center py-8">
              <PulseLoader size={4} />
            </div>
          ) : stages.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-md text-gray-placeholder dark:text-gray-placeholder-dk">
                No lead stages available
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {stages.map((stage) => {
                const isSelected = selectedStages.includes(stage?.code);
                return (
                  <button
                    key={stage?.code}
                    type="button"
                    onClick={() => handleToggleStage(stage?.code)}
                    className={`flex h-[36px] items-center justify-between pl-2 pr-3 py-2.5 rounded-md border w-full cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-card-highlight-2 dark:bg-card-highlight-2-dk border-selected-option-highlight dark:border-selected-option-highlight-dk"
                        : "border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                    }`}
                  >
                    <span className="flex flex-1 items-center gap-1 h-[20px] rounded-md min-w-0">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: stage?.base_color || "#666" }}
                      />
                      <TruncatedTextTooltip
                        text={stage?.name ?? ""}
                        className="text-xs font-semibold uppercase text-verba-indigo dark:text-verba-indigo-dk leading-[18px]"
                        maxWidth="max-w-full"
                        side="left"
                      />
                    </span>
                    <span
                      className={`shrink-0 w-[14px] h-[14px] border rounded-[3px] flex items-center justify-center ml-2 ${
                        isSelected
                          ? "bg-verba-blue dark:bg-verba-blue-dk border-verba-blue dark:border-verba-blue-dk"
                          : "border-gray-placeholder dark:border-gray-placeholder-dk bg-pure-white dark:bg-pure-white-dk"
                      }`}
                    >
                      {isSelected && (
                        <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4 px-3 pb-2 shrink-0">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={isApplyDisabled}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default LeadStageFilter;
